import r2wc from "@r2wc/react-to-web-component";
import { useState, useEffect, useMemo } from "react";
import { parseCssVariables } from "./utils/parseCssVariables";
import "./styles/App.scss";
import "./styles/fonts-heading.scss";
import "./styles/manage-order.scss";
import "./styles/tailwind.css";
// The /secure-your-booking, /place-your-order, /manage-your-order, /confirm and
// /book routes paint the design (see AppRouter).
import "./styles/secure-your-booking.scss";
import "./styles/place-your-order.scss";
import "./styles/manage-your-order.scss";
import "./styles/confirm-payment.scss";
import "./styles/book.scss";

import "./styles/form.scss";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import i18n from "../i18n";
import {
  GuestCheckoutSettingsProvider,
  mergeGuestCheckoutSettings,
} from "./components/context/GuestCheckoutSettingsContext";

import SecureYourBooking from "./components/secureYourBooking";
import { ManageYourOrder } from "./components/manageYourOrder";
import { PlaceYourOrder } from "./components/placeYourOrder";
import Layout from "./components/layout/Layout";
import { ThemedSurface } from "./components/layout/ThemedSurface";
import SignatureGuard from "./components/SignatureGuard";
import ConfirmPayment from "./components/ConfirmPayment";
import NotFoundPage from "./components/NotFoundPage";
import BookingForm from "./components/bookingForm/BookingForm";
import { initialiseBugsnag } from "./bugsnag";
import { bootstrapTelemetry } from "./telemetry";
import { useCookieConsent } from "./components/hooks/useCookieConsent";
import CookiePreferences from "./components/shared/CookiePreferences";
import { PaymentRedirectProvider } from "./components/context/PaymentRedirectContext";

// Started before React renders so the SDK captures the initial page view and any
// early network requests and errors. Nothing is sent until the guest has
// consented to non-essential cookies.
bootstrapTelemetry();

interface AppRouterProps {
  /**
   * The venue's `--gc-*` declarations, already applied inline on the app root.
   * A migrated route nests a ThemedSurface whose token map re-declares the
   * `--gc-color-*` variables, and a declaration on a child replaces an
   * inherited one however it was set — inline on an ancestor included. Passing
   * them down puts them inline on the surface itself, where they win again, so
   * the route honours venue colours and fonts like every other route.
   */
  readonly customStyles?: string;
}

function AppRouter({ customStyles }: Readonly<AppRouterProps>) {
  return (
    <Router>
      <Routes>
        <Route
          path="/secure-your-booking"
          element={
            /*
             * Migrated to the design, like /place-your-order below, while the
             * rest of the app stays on the legacy look. `theme="light"` is the
             * default rather than `auto` — guests reach this page from a link
             * Collins emails them, so the dark scheme is opt-in via `?theme=`
             * (which takes precedence) until every flow is dark-verified.
             *
             * ThemedSurface wraps SignatureGuard rather than sitting inside
             * SecureYourBookingWrapper, so the guard's own loading and refusal
             * states are themed too.
             */
            <ThemedSurface id="secure-your-booking-app" theme="light" customStyles={customStyles}>
              <SignatureGuard>
                <SecureYourBookingWrapper />
              </SignatureGuard>
            </ThemedSurface>
          }
        />
        <Route
          path="/manage-your-order"
          element={
            /*
             * Defaults to `light` for the same reason as the two routes
             * around it: guests arrive from an emailed link rather than a
             * venue page that has already chosen a scheme, so `?theme=dark`
             * opts in.
             */
            <ThemedSurface id="manage-your-order-app" theme="light" customStyles={customStyles}>
              <SignatureGuard>
                <ManageYourOrderWrapper />
              </SignatureGuard>
            </ThemedSurface>
          }
        />
        <Route
          path="/place-your-order"
          element={
            /*
             * Defaults to `light` for the same reason as /secure-your-booking
             * above: guests arrive from an emailed link rather than a venue
             * page that has already chosen a scheme, so `?theme=dark` opts in.
             */
            <ThemedSurface id="place-your-order-app" theme="light" customStyles={customStyles}>
              <SignatureGuard>
                <PlaceYourOrderWrapper />
              </SignatureGuard>
            </ThemedSurface>
          }
        />
        <Route
          path="/confirm"
          element={
            <ThemedSurface id="confirm-payment-app" theme="light" customStyles={customStyles}>
              <SignatureGuard optional>
                <ConfirmPayment allowReturnRedirect />
              </SignatureGuard>
            </ThemedSurface>
          }
        />
        <Route
          path="/book"
          element={
            /*
             * The booking journey's hosted surface, painting the design the
             * `<booking-form>` widget introduced (book.scss).
             *
             * Takes the design's own light palette, like the four routes
             * above, rather than the widget's `legacy` one. The widget keeps
             * `legacy` because its light rendering was signed off against the
             * pre-token palette; a hosted page has no such constraint, and
             * the legacy secondary inks are a poor fit here — they put a teal
             * on the Back and Change Details links that belongs to neither
             * scheme, and leave input placeholders at 2.2:1, short of WCAG
             * 1.4.3. So this route reads as one of the hosted five rather
             * than as the widget.
             *
             * Defaults to `light` for the same reason as the four routes
             * above: a guest arrives from a venue's link rather than a page
             * that has already chosen a scheme, so `?theme=dark` opts in.
             */
            <ThemedSurface id="book-app" theme="light" customStyles={customStyles}>
              <BookingForm />
            </ThemedSurface>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

export function getBookingIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("booking_id") ?? "";
}

export function getOrderIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("order_id") ?? undefined;
}

export function SecureYourBookingWrapper() {
  const [bookingId, setBookingId] = useState("");
  useEffect(() => {
    setBookingId(getBookingIdFromUrl());
  }, []);
  return bookingId ? (
    <SecureYourBooking bookingId={bookingId} />
  ) : (
    <Layout showHeader main={<span>Loading...</span>} aside={undefined} />
  );
}

export function ManageYourOrderWrapper() {
  const [bookingId, setBookingId] = useState("");
  useEffect(() => {
    setBookingId(getBookingIdFromUrl());
  }, []);
  return bookingId ? (
    // Keyed by booking, so another booking starts from a clean component
    // rather than inheriting the previous one's orders and stage.
    <ManageYourOrder key={bookingId} bookingId={bookingId} />
  ) : (
    <Layout showHeader main={<span>Loading...</span>} aside={undefined} />
  );
}

export function PlaceYourOrderWrapper() {
  const [bookingId, setBookingId] = useState("");
  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  useEffect(() => {
    setBookingId(getBookingIdFromUrl());
    setOrderId(getOrderIdFromUrl());
  }, []);
  return bookingId ? (
    <PlaceYourOrder bookingId={bookingId} orderId={orderId} />
  ) : (
    <Layout showHeader main={<span>Loading...</span>} aside={undefined} />
  );
}

interface AppProps {
  /**
   * CSS custom property overrides for venue theming.
   *
   * Pass a plain CSS string containing `--gc-*` variable declarations.
   * These cascade into all child components, allowing venues to customise
   * colours, fonts, and layout without touching internal styles.
   *
   * See `SecureYourBookingElement.tsx` for the full list of supported variables.
   *
   * Example (HTML attribute):
   *   style="--gc-color-primary: #C0392B; --gc-border-radius-button: 4px;"
   */
  readonly theme?: string;
  /** MFE attribute: hide the offer selector. */
  readonly hideOffers?: string;
  /** MFE attribute: enable venue searching (accepted for parity; not yet surfaced in the UI). */
  readonly searchVenues?: string;
  /** MFE attribute: show the booking type selector before the date/covers step. */
  readonly showTypeFirst?: string;
  /** MFE attribute: locale code for i18n/API calls. */
  readonly locale?: string;
  /** MFE attribute: comma-separated venue IDs for multi-venue selection. */
  readonly venues?: string;
  /** MFE attribute: comma-separated booking type IDs to restrict the type selector. */
  readonly allowedTypes?: string;
  readonly redirectUrl?: string;
}

export function App({
  theme,
  hideOffers,
  searchVenues,
  showTypeFirst,
  locale,
  venues,
  allowedTypes,
  redirectUrl,
}: AppProps = {}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  useCookieConsent();

  const settings = useMemo(
    () =>
      mergeGuestCheckoutSettings({
        hideOffers,
        searchVenues,
        showTypeFirst,
        locale,
        venues,
        allowedTypes,
      }),
    [hideOffers, searchVenues, showTypeFirst, locale, venues, allowedTypes],
  );

  useEffect(() => {
    if (settings.locale) {
      void i18n.changeLanguage(settings.locale);
    }
  }, [settings.locale]);

  useEffect(() => {
    initialiseBugsnag();
  }, []);

  useEffect(() => {
    let didLoad = false;
    const onLoad = () => {
      didLoad = true;
      setLoading(false);
    };

    // If document already loaded, hide loader immediately
    if (document.readyState === "complete") {
      onLoad();
      return;
    }

    window.addEventListener("load", onLoad);

    // Safety fallback: hide loader after 5s in case load event doesn't fire
    const timeout = setTimeout(() => {
      if (!didLoad) setLoading(false);
    }, 5000);

    return () => {
      window.removeEventListener("load", onLoad);
      clearTimeout(timeout);
    };
  }, []);

  return (
    // The `theme` prop is a CSS string of --gc-* variable declarations supplied
    // by the embedding venue. Applying it as an inline style on the root div
    // makes every declaration cascade into all descendant components.
    <PaymentRedirectProvider redirectUrl={redirectUrl}>
      <GuestCheckoutSettingsProvider value={settings}>
        <div id="guest-checkout-app" style={parseCssVariables(theme)}>
          {loading && (
            <output className="page-loader" aria-live="polite" aria-label={t("loading")}>
              <div className="loading-spinner" />
            </output>
          )}
          <AppRouter customStyles={theme} />
          <CookiePreferences />
        </div>
      </GuestCheckoutSettingsProvider>
    </PaymentRedirectProvider>
  );
}

const Component = r2wc(App, {
  props: {
    theme: "string",
    hideOffers: "string",
    searchVenues: "string",
    showTypeFirst: "string",
    locale: "string",
    venues: "string",
    allowedTypes: "string",
    redirectUrl: "string",
  },
});

if (!window.customElements.get("guest-checkout")) {
  window.customElements.define("guest-checkout", Component);
}

export default Component;
